/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from '@/components/link';
import type { FooterCta } from '@/lib/content/types';

/**
 * Renders a real CTA honouring both `FooterCta` union variants. The `modal` variant is temporarily
 * inert: it previously revealed an in-place signup form (`signup.tsx`), but that form was a
 * placeholder for a real modal whose design/integration is still undecided (see design.md), so it
 * has been removed. The button renders and is clickable — matching the "never inert" look — but has
 * no handler yet. Wire the real modal's open trigger in here once its design lands.
 */
export default function NewsletterCta({ cta }: { cta: FooterCta }): ReactElement {
    if (cta.action === 'link') {
        return (
            <Button asChild variant="white-outline" type="button">
                <Link to={cta.url}>{cta.label}</Link>
            </Button>
        );
    }

    return (
        <Button variant="white-outline" type="button">
            {cta.label}
        </Button>
    );
}
